import { Injectable, signal, computed } from '@angular/core';
import { Toast, ToastOptions, ToastType } from './toast.model';

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    // Signal storing all active toasts
    private readonly toasts = signal<Toast[]>([]);

    // Computed signal for accessing toasts (read-only)
    readonly activeToasts = computed(() => this.toasts());

    // Default duration in milliseconds
    private readonly defaultDuration = 10000;

    // Animation duration in milliseconds
    private readonly animationDuration = 300;

    // Track toasts that are exiting (for animation)
    private exitingToasts = new Set<string>();

    // Track pending timeouts for cleanup
    private readonly dismissTimeouts = new Map<
        string,
        ReturnType<typeof setTimeout>
    >();
    private readonly animationTimeouts = new Map<
        string,
        ReturnType<typeof setTimeout>
    >();

    /**
     * Generate unique ID for toast
     */
    private generateId(): string {
        return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    }

    /**
     * Check if a toast is exiting
     */
    isExiting(id: string): boolean {
        return this.exitingToasts.has(id);
    }

    /**
     * Mark toast as exiting and schedule removal
     */
    dismiss(id: string): void {
        if (this.exitingToasts.has(id)) return; // Already exiting

        // Clear any pending auto-dismiss timeout
        const existingTimeout = this.dismissTimeouts.get(id);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            this.dismissTimeouts.delete(id);
        }

        this.exitingToasts.add(id);

        // Trigger re-render to apply exit animation
        this.toasts.update((current) => [...current]);

        // Remove after animation completes
        const animationTimeout = setTimeout(() => {
            this.remove(id);
            this.exitingToasts.delete(id);
            this.animationTimeouts.delete(id);
        }, this.animationDuration);
        this.animationTimeouts.set(id, animationTimeout);
    }

    /**
     * Add a toast to the stack
     */
    private add(
        message: string,
        type: ToastType,
        duration: number = this.defaultDuration,
    ): void {
        const toast: Toast = {
            id: this.generateId(),
            message,
            type,
            duration,
        };

        this.toasts.update((current) => [...current, toast]);

        // Auto-dismiss after duration
        if (duration > 0) {
            const dismissTimeout = setTimeout(() => {
                this.dismiss(toast.id);
                this.dismissTimeouts.delete(toast.id);
            }, duration);
            this.dismissTimeouts.set(toast.id, dismissTimeout);
        }
    }

    /**
     * Remove a toast by ID (immediate, no animation)
     */
    remove(id: string): void {
        // Clear any pending timeouts
        const dismissTimeout = this.dismissTimeouts.get(id);
        if (dismissTimeout) {
            clearTimeout(dismissTimeout);
            this.dismissTimeouts.delete(id);
        }
        const animationTimeout = this.animationTimeouts.get(id);
        if (animationTimeout) {
            clearTimeout(animationTimeout);
            this.animationTimeouts.delete(id);
        }

        this.toasts.update((current) => current.filter((t) => t.id !== id));
        this.exitingToasts.delete(id);
    }

    /**
     * Remove all toasts
     */
    clear(): void {
        // Clear all pending timeouts
        for (const timeout of this.dismissTimeouts.values()) {
            clearTimeout(timeout);
        }
        for (const timeout of this.animationTimeouts.values()) {
            clearTimeout(timeout);
        }
        this.dismissTimeouts.clear();
        this.animationTimeouts.clear();

        this.toasts.set([]);
        this.exitingToasts.clear();
    }

    /**
     * Show success toast
     */
    success(message: string, duration?: number): void {
        this.add(message, 'success', duration);
    }

    /**
     * Show error toast
     */
    error(message: string, duration?: number): void {
        this.add(message, 'error', duration);
    }

    /**
     * Show warning toast
     */
    warning(message: string, duration?: number): void {
        this.add(message, 'warning', duration);
    }

    /**
     * Show info toast
     */
    info(message: string, duration?: number): void {
        this.add(message, 'info', duration);
    }

    /**
     * Create toast with custom options
     */
    show(message: string, options: ToastOptions = {}): void {
        const type = options.type ?? 'info';
        const duration = options.duration ?? this.defaultDuration;
        this.add(message, type, duration);
    }
}
